import { useState, useEffect, useRef, useMemo } from "react";
import { useApp } from "../context/AppContext";
import { imagingAPI, getInitials, getAvatarColor, customModalitiesAPI, imagingModalityAPI } from "../utils/api";
import DicomViewer from "../components/DicomViewer";
import { useTheme, toBengaliNum } from "../context/ThemeContext";

const MODALITIES = ["X-Ray", "CT", "MRI", "Ultrasound", "Mammography", "ECG", "Echo", "Color Doppler", "OPG", "DEXA/BMD", "PET-CT", "Custom..."];
const DATE_OPTIONS = ["All", "Today", "This Week", "This Month", "This Year"];
const BLOOD_GROUPS = ["A+","A-","B+","B-","AB+","AB-","O+","O-","Unknown"];
const PAGE_SIZE = 20;

const MODALITY_COLOR = {
  "X-Ray":        { color: "#4F46E5", bg: "#EEF2FF" },
  "CT":           { color: "#0D9488", bg: "#CCFBF1" },
  "MRI":          { color: "#7C3AED", bg: "#F5F3FF" },
  "Ultrasound":   { color: "#D97706", bg: "#FEF3C7" },
  "Mammography":  { color: "#DB2777", bg: "#FDF2F8" },
  "ECG":          { color: "#059669", bg: "#DCFCE7" },
  "Echo":         { color: "#0284C7", bg: "#E0F2FE" },
  "Color Doppler":{ color: "#EA580C", bg: "#FFF7ED" },
  "OPG":          { color: "#65A30D", bg: "#F0FDF4" },
  "DEXA/BMD":     { color: "#9333EA", bg: "#FAF5FF" },
  "PET-CT":       { color: "#DC2626", bg: "#FEF2F2" },
  "Other":        { color: "#64748B", bg: "#F1F5F9" },
};

function getDateRange(filter) {
  const now = new Date();
  const today = now.toISOString().split("T")[0];
  if (filter === "Today") return { from: today, to: today };
  if (filter === "This Week")  { const d = new Date(now); d.setDate(d.getDate()-7); return { from: d.toISOString().split("T")[0], to: today }; }
  if (filter === "This Month") { const d = new Date(now.getFullYear(), now.getMonth(), 1); return { from: d.toISOString().split("T")[0], to: today }; }
  if (filter === "This Year")  { const d = new Date(now.getFullYear(), 0, 1); return { from: d.toISOString().split("T")[0], to: today }; }
  return null;
}

function validatePhoneDigits(digits) {
  const d = digits.trim().replace(/[\s\-()]/g, "");
  if (!d) return { valid: false, message: "Phone is required." };
  if (!/^\d{10}$/.test(d)) return { valid: false, message: "Enter 10 digits after +880." };
  if (!/^1[3-9]\d{8}$/.test(d)) return { valid: false, message: "Must start with 13–19." };
  return { valid: true, normalised: "+880" + d };
}

function ModalityBadge({ modality }) {
  const mc = MODALITY_COLOR[modality] || MODALITY_COLOR.Other;
  return (
    <span style={{ background: mc.bg, color: mc.color, padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 700, marginRight: 4 }}>
      {modality}
    </span>
  );
}

export default function Imaging() {
  const { t, language } = useTheme();
  const { patients, user, addPatient, addImaging, deleteImaging, updateImaging, getImagingFileUrl } = useApp();

  // ── Level state: "patients" | "studies" | "viewer" ───────────────────────
  const [level, setLevel] = useState(() => {
    const saved = sessionStorage.getItem("emr_img_level");
    // Start at "studies" on restore — viewer needs viewImg which loads async
    // openStudy() will switch to "viewer" once image token is ready
    if (saved === "viewer") return "studies";
    return saved || "patients";
  });
  const [selectedPatient,   setSelectedPatient]   = useState(null); // { id, name }
  const [patientStudies,    setPatientStudies]     = useState([]);
  const [studiesLoading,    setStudiesLoading]     = useState(false);
  const [viewImg,           setViewImg]            = useState(null);
  const [cameFromPatient,   setCameFromPatient]    = useState(null); // { id, name } if jumped from PatientDetail

  // ── Patient list state ───────────────────────────────────────────────────
  const [allImaging,   setAllImaging]   = useState([]);
  const [loading,      setLoading]      = useState(() => {
    const saved = sessionStorage.getItem("emr_img_level");
    return saved && saved !== "patients";
  });
  const [search,       setSearch]       = useState("");
  const [page,         setPage]         = useState(1);

  // ── Viewer state ─────────────────────────────────────────────────────────
  const [zoom,         setZoom]         = useState(1);
  const [editFindings, setEditFindings] = useState(null);
  const [findingsText, setFindingsText] = useState("");
  const [confirmDel,   setConfirmDel]   = useState(null);
  const [showUpload,   setShowUpload]   = useState(false);
  const imgRef   = useRef(null);
  const dicomRef = useRef(null);

  const canEdit   = user?.role === "doctor" || user?.role === "admin";
  const canUpload = ["technician","doctor","admin"].includes(user?.role);

  // ── Load all imaging once (for patient grouping) ─────────────────────────
  useEffect(() => {
    setLoading(true);
    imagingAPI.listAll(1, "").then(data => {
      const all = (data.imaging || []).map(img => ({
        ...img,
        patientName: img.patient_name || img.patientName || "",
        patientId:   img.patient_id  || img.patientId  || "",
      }));
      setAllImaging(all);

      // Restore session state on reload
      const savedLevel   = sessionStorage.getItem("emr_img_level");
      const savedPatId   = sessionStorage.getItem("emr_img_patient_id");
      const savedPatName = sessionStorage.getItem("emr_img_patient_name");
      const savedStudyId = sessionStorage.getItem("emr_img_study_id");
      if (savedLevel && savedLevel !== "patients" && savedPatId) {
        // Keep loading=true until patient restore is done — prevents flash
        setSelectedPatient({ id: savedPatId, name: savedPatName || savedPatId });
        setCameFromPatient(null);
        setStudiesLoading(true);
        setLevel(savedLevel);
        imagingAPI.getForPatient(savedPatId).then(res => {
          const imgs = Array.isArray(res) ? res : (res.imaging || []);
          const studies = imgs.map(img => ({ ...img, patientName: savedPatName, patientId: savedPatId }));
          setPatientStudies(studies);
          if (savedLevel === "viewer" && savedStudyId) {
            const study = studies.find(s => s.id === savedStudyId);
            if (study) {
              setLevel("studies"); // show studies shell (loading state) while openStudy fetches token
              // studiesLoading stays true — hides study rows until viewer is ready
              openStudy(study);
            } else {
              setStudiesLoading(false);
              setLevel("studies");
              sessionStorage.setItem("emr_img_level", "studies");
            }
          } else {
            setStudiesLoading(false);
          }
          setLoading(false);
        }).catch(() => { setStudiesLoading(false); setLoading(false); setLevel("patients"); });
      } else {
        setLoading(false);
      }

      // Auto-select patient if navigated here from PatientDetail
      const jump = sessionStorage.getItem("emr_img_patient");
      if (jump) {
        sessionStorage.removeItem("emr_img_patient");
        try {
          const { id, name } = JSON.parse(jump);
          if (id) {
            setSelectedPatient({ id, name });
            setCameFromPatient({ id, name });
            setLevel("studies");
            setStudiesLoading(true);
            imagingAPI.getForPatient(id).then(res => {
              const imgs = Array.isArray(res) ? res : (res.imaging || []);
              setPatientStudies(imgs.map(img => ({
                ...img,
                patientName: name,
                patientId:   id,
              })));
              setStudiesLoading(false);
            }).catch(() => { setPatientStudies([]); setStudiesLoading(false); });
          }
        } catch (e) {}
      }
    }).catch(() => setLoading(false));
  }, []);

  // ── Group imaging by patient ─────────────────────────────────────────────
  const patientGroups = useMemo(() => {
    const q = search.toLowerCase();
    const map = {};
    allImaging.forEach(img => {
      if (q && !(
        img.patientName?.toLowerCase().includes(q) ||
        img.patientId?.toLowerCase().includes(q) ||
        img.modality?.toLowerCase().includes(q)
      )) return;
      if (!map[img.patientId]) {
        map[img.patientId] = {
          patientId:   img.patientId,
          patientName: img.patientName,
          studies:     [],
          modalities:  new Set(),
          lastDate:    img.date,
        };
      }
      map[img.patientId].studies.push(img);
      map[img.patientId].modalities.add(img.modality);
      if (img.date > map[img.patientId].lastDate) map[img.patientId].lastDate = img.date;
    });
    return Object.values(map).sort((a, b) => b.lastDate.localeCompare(a.lastDate));
  }, [allImaging, search]);

  // Pagination
  const totalPages  = Math.max(1, Math.ceil(patientGroups.length / PAGE_SIZE));
  const pagedGroups = patientGroups.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const stats = useMemo(() => ({
    patients:  patientGroups.length,
    studies:   allImaging.length,
    today:     allImaging.filter(i => i.date === new Date().toISOString().split("T")[0]).length,
    modalities: [...new Set(allImaging.map(i => i.modality))].length,
  }), [allImaging, patientGroups]);

  // ── Open patient studies ─────────────────────────────────────────────────
  const openPatient = async (group) => {
    setSelectedPatient({ id: group.patientId, name: group.patientName });
    sessionStorage.setItem("emr_img_level", "studies");
    sessionStorage.setItem("emr_img_patient_id", group.patientId);
    sessionStorage.setItem("emr_img_patient_name", group.patientName || "");
    setStudiesLoading(true);
    setLevel("studies");
    try {
      const data = await imagingAPI.getForPatient(group.patientId);
      setPatientStudies(data.map ? data.map(img => ({
        ...img,
        patientName: group.patientName,
        patientId:   group.patientId,
      })) : []);
    } catch { setPatientStudies([]); }
    setStudiesLoading(false);
  };

  const openStudy = async (img) => {
    setZoom(1);
    // Fetch dtok BEFORE rendering the viewer so DicomViewer gets the URL with auth
    try {
      const BASE = process.env.REACT_APP_API_URL || (window.location.hostname === "localhost" ? "http://localhost:8000" : "https://nashman-prod-production-c773.up.railway.app");
      const res = await fetch(BASE + "/auth/download-token", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": "Bearer " + localStorage.getItem("emr_token") },
      });
      if (res.ok) {
        const { token: dtok } = await res.json();
        const fileUrl = (img.storage_url || (BASE + "/imaging/" + img.id + "/file")) + "?dtok=" + dtok;
        setViewImg({ ...img, storage_url: fileUrl });
        sessionStorage.setItem("emr_img_level", "viewer");
        sessionStorage.setItem("emr_img_study_id", img.id);
      } else {
        setViewImg(img); // fallback — viewer will show auth error
        sessionStorage.setItem("emr_img_level", "viewer");
        sessionStorage.setItem("emr_img_study_id", img.id);
      }
    } catch (e) {
      console.error("Could not get download token for imaging file", e);
      setViewImg(img); // fallback
    }
    setStudiesLoading(false);
    setLevel("viewer"); // open viewer only after token is set
  };

  const backToPatients = () => {
    ["emr_img_level","emr_img_patient_id","emr_img_patient_name","emr_img_study_id"].forEach(k => sessionStorage.removeItem(k));
    setLevel("patients");
    setSelectedPatient(null);
    setPatientStudies([]);
  };

  const backToStudies = () => {
    sessionStorage.setItem("emr_img_level", "studies");
    sessionStorage.removeItem("emr_img_study_id");
    setLevel("studies");
    setViewImg(null);
    setZoom(1);
    setEditFindings(null);
  };

  const handleDelete = async (id) => {
    await deleteImaging(id);
    setAllImaging(prev => prev.filter(i => i.id !== id));
    setPatientStudies(prev => prev.filter(i => i.id !== id));
    if (viewImg?.id === id) backToStudies();
    setConfirmDel(null);
  };

  const handleSaveFindings = async (img) => {
    await updateImaging(img.id, { findings: findingsText });
    if (viewImg?.id === img.id) setViewImg(v => ({ ...v, findings: findingsText }));
    setPatientStudies(prev => prev.map(i => i.id === img.id ? { ...i, findings: findingsText } : i));
    setAllImaging(prev => prev.map(i => i.id === img.id ? { ...i, findings: findingsText } : i));
    setEditFindings(null);
  };

  const isImageFile = (path) => /\.(jpg|jpeg|png|webp|bmp|gif|tiff)$/i.test(path || "");
  const isDicomFile = (path) => /\.dcm$/i.test(path || "");
  const isPdfFile   = (path) => /\.pdf$/i.test(path || "");

  const filterBtn = (active) => ({
    padding: "4px 12px", borderRadius: 99, fontSize: 12, cursor: "pointer", border: "1px solid",
    background: active ? "#0F172A" : "#fff",
    borderColor: active ? "#0F172A" : "#E4E8F0",
    color: active ? "#fff" : "#475569", fontWeight: active ? 700 : 400,
  });

  // ════════════════════════════════════════════════════════════════════════════
  // LEVEL 3: Viewer
  // ════════════════════════════════════════════════════════════════════════════
  if (level === "viewer" && viewImg) {
    const mc      = MODALITY_COLOR[viewImg.modality] || MODALITY_COLOR.Other;
    const fileUrl = viewImg.storage_url || getImagingFileUrl(viewImg.id);  // storage_url already includes ?dtok= from openStudy
    const isImg   = isImageFile(viewImg.file_name);
    const isDicom = isDicomFile(viewImg.file_name);
    const isPdf   = isPdfFile(viewImg.file_name);

    return (
      <div>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => {
            if (cameFromPatient) {
              sessionStorage.setItem("emr_selected_patient", cameFromPatient.id);
              sessionStorage.setItem("pt_pd_view", "categories");
              sessionStorage.removeItem("pt_pd_type");
              sessionStorage.removeItem("pt_pd_rep");
              // Clear imaging session so patient tab starts fresh
              ["emr_img_level","emr_img_patient_id","emr_img_patient_name","emr_img_study_id"].forEach(k => sessionStorage.removeItem(k));
              setCameFromPatient(null);
              window.dispatchEvent(new CustomEvent("emr:navigate", { detail: { page: "patients" } }));
            } else {
              backToStudies();
            }
          }}>← Back</button>
          <div style={{ flex:1 }}>
            <h2 style={{ fontSize:17, fontWeight:700, marginBottom:2 }}>{viewImg.modality} — {viewImg.patientName}</h2>
            <div style={{ fontSize:12, color:"#64748B" }}>{viewImg.date}{viewImg.time ? ` · ${viewImg.time}` : ""}</div>
          </div>
          {canEdit && <button className="btn btn-danger btn-sm" onClick={() => setConfirmDel(viewImg.id)}>{t.deleteBtn}</button>}
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 340px", gap:18 }}>
          <div className="card" style={{ overflow:"hidden" }}>
            <div className="card-header">
              <div style={{ fontWeight:700, fontSize:14 }}>
                <span style={{ background:mc.bg, color:mc.color, padding:"2px 10px", borderRadius:6, fontSize:12, fontWeight:700, marginRight:8 }}>{viewImg.modality}</span>
                {viewImg.file_name}
              </div>
              <div style={{ display:"flex", gap:6 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setZoom(z => Math.max(0.5,z-0.25))}>−</button>
                <span style={{ fontSize:12, padding:"4px 8px", color:"#475569" }}>{Math.round(zoom*100)}%</span>
                <button className="btn btn-ghost btn-sm" onClick={() => setZoom(z => Math.min(4,z+0.25))}>+</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setZoom(1)}>Reset</button>
                {isDicom
                  ? <button className="btn btn-secondary btn-sm" onClick={() => dicomRef.current?.openInNewTab()}>Open</button>
                  : <button className="btn btn-secondary btn-sm" onClick={async () => {
                      try {
                        const BASE = process.env.REACT_APP_API_URL || (window.location.hostname === "localhost" ? "http://localhost:8000" : "https://nashman-prod-production-c773.up.railway.app");
                        const res = await fetch(BASE + "/auth/download-token", {
                          method: "POST",
                          headers: { "Content-Type": "application/json", "Authorization": "Bearer " + localStorage.getItem("emr_token") },
                        });
                        if (res.ok) {
                          const { token: dtok } = await res.json();
                          const base = viewImg.file_name
                            ? (BASE + "/imaging/" + viewImg.id + "/file")
                            : fileUrl.split("?")[0];
                          window.open(base + "?dtok=" + dtok, "_blank");
                        }
                      } catch (e) {
                        window.open(fileUrl, "_blank");
                      }
                    }}>Open</button>
                }
              </div>
            </div>
            <div style={{ background:"#0F172A", minHeight:480, overflow:"auto", padding: isDicom ? 0 : isPdf ? 0 : 24, display:"flex", alignItems: isDicom ? "center" : "flex-start", justifyContent:"center" }}>
              {isDicom ? (
                <DicomViewer ref={dicomRef} fileUrl={fileUrl} zoom={zoom} />
              ) : isImg ? (
                <img ref={imgRef} src={fileUrl} alt={viewImg.description||viewImg.modality}
                  style={{ transform:`scale(${zoom})`, transformOrigin:"top left", maxWidth:"none", transition:"transform 0.15s", imageRendering:zoom>2?"pixelated":"auto" }}
                  onError={e => { e.target.style.display="none"; }} />
              ) : isPdf ? (
                <iframe
                  src={fileUrl}
                  title={viewImg.file_name}
                  style={{ width:"100%", minHeight:640, border:"none", background:"#fff" }}
                />
              ) : (
                <div style={{ textAlign:"center", color:"#94A3B8", width:"100%" }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="64" height="64"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  <div style={{ fontSize:14, marginBottom:8 }}>{viewImg.file_name}</div>
                  <a href={fileUrl} target="_blank" rel="noreferrer" className="btn btn-primary btn-sm">Download / Open</a>
                </div>
              )}
            </div>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            <div className="card">
              <div className="card-body">
                <div style={{ fontSize:11, fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:10 }}>Study Info</div>
                {[["Patient",viewImg.patientName],["EMR ID",viewImg.patientId],["Modality",viewImg.modality],["Date",viewImg.date],["Time",viewImg.time||"—"],["Description",viewImg.description||"—"],["Radiologist",viewImg.radiologist||"—"]].map(([k,v]) => (
                  <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", borderBottom:"1px solid #F1F5F9", fontSize:13 }}>
                    <span style={{ color:"#64748B" }}>{k}</span>
                    <span style={{ fontWeight:600, textAlign:"right", maxWidth:180 }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="card" style={{ flex:1 }}>
              <div className="card-header">
                <div style={{ fontWeight:700, fontSize:14 }}>{language === "bn" ? "রেডিওলজিস্ট নোট / ফলাফল" : "Radiologist Notes / Findings"}</div>
                {canEdit && editFindings !== viewImg.id && (
                  <button className="btn btn-secondary btn-sm" onClick={() => { setEditFindings(viewImg.id); setFindingsText(viewImg.findings||""); }}>{t.edit}</button>
                )}
              </div>
              <div className="card-body">
                {editFindings === viewImg.id ? (
                  <div>
                    <textarea className="input" value={findingsText} onChange={e => setFindingsText(e.target.value)} rows={8} style={{ resize:"vertical", fontSize:13 }} placeholder="Enter radiologist findings..." autoFocus />
                    <div style={{ display:"flex", gap:8, marginTop:10 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditFindings(null)}>{t.cancel}</button>
                      <button className="btn btn-primary btn-sm" onClick={() => handleSaveFindings(viewImg)}>{language === "bn" ? "ফলাফল সংরক্ষণ" : "Save Findings"}</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ whiteSpace:"pre-wrap", fontSize:13, color:viewImg.findings?"#0F172A":"#94A3B8", lineHeight:1.6 }}>
                    {viewImg.findings || (language === "bn" ? "কোনো ফলাফল লিপিবদ্ধ নেই।" : "No findings recorded yet.")}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {confirmDel && (
          <div className="modal-overlay">
            <div className="modal" style={{ maxWidth:380 }}>
              <div className="modal-header"><div style={{ fontWeight:700 }}>{language === "bn" ? "ইমেজিং রেকর্ড মুছুন" : "Delete Imaging Record"}</div></div>
              <div className="modal-body"><div className="alert alert-red">This will permanently delete the image and all associated data.</div></div>
              <div className="modal-footer">
                <button className="btn btn-ghost" onClick={() => setConfirmDel(null)}>{t.cancel}</button>
                <button className="btn btn-danger" onClick={() => handleDelete(confirmDel)}>{t.deleteBtn}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // LEVEL 2: Patient's studies
  // ════════════════════════════════════════════════════════════════════════════
  if (level === "studies" && selectedPatient) {
    return (
      <div>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20 }}>
          <button className="btn btn-ghost btn-sm" onClick={backToPatients}>← Back</button>
          <div style={{ flex:1 }}>
            <h2 style={{ fontSize:17, fontWeight:700, marginBottom:2 }}>{selectedPatient.name}</h2>
            <div style={{ fontSize:12, color:"#64748B" }}>{selectedPatient.id} · Imaging Studies</div>
          </div>
          {canUpload && (
            <button className="btn btn-primary btn-sm" onClick={() => setShowUpload(true)}>{language === "bn" ? "+ আপলোড" : "+ Upload"}</button>
          )}
        </div>

        <div className="card">
          {studiesLoading ? (
            <div className="empty-state"><p>Loading studies...</p></div>
          ) : patientStudies.length === 0 ? (
            <div className="empty-state">
              <h3>No imaging studies</h3>
              <p>No imaging records found for this patient.</p>
            </div>
          ) : (
            <div>
              <div style={{ display:"grid", gridTemplateColumns:"100px 120px 1fr 120px 80px", padding:"10px 20px", background:"#F8F9FC", borderBottom:"2px solid #E4E8F0" }}>
                {(language === "bn" ? ["তারিখ","মোডালিটি","বিবরণ / ফলাফল","রেডিওলজিস্ট","কার্যক্রম"] : ["Date","Modality","Description / Findings","Radiologist","Action"]).map(h => (
                  <div key={h} style={{ fontSize:11, fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.5px" }}>{h}</div>
                ))}
              </div>
              {patientStudies.map((img, idx) => {
                const mc = MODALITY_COLOR[img.modality] || MODALITY_COLOR.Other;
                return (
                  <div key={img.id} style={{
                    display:"grid", gridTemplateColumns:"100px 120px 1fr 120px 80px",
                    padding:"12px 20px", borderBottom:"1px solid #F1F5F9",
                    background: idx%2===0 ? "#fff" : "#FAFBFC",
                    alignItems:"center", cursor:"pointer", transition:"background 0.12s",
                  }}
                    onMouseEnter={e => e.currentTarget.style.background="#EFF6FF"}
                    onMouseLeave={e => e.currentTarget.style.background=idx%2===0?"#fff":"#FAFBFC"}
                    onClick={() => openStudy(img)}
                  >
                    <div style={{ fontSize:12, fontWeight:600, color:"#475569" }}>{img.date}</div>
                    <div><span style={{ background:mc.bg, color:mc.color, padding:"2px 8px", borderRadius:99, fontSize:11, fontWeight:700 }}>{img.modality}</span></div>
                    <div>
                      <div style={{ fontSize:13, fontWeight:600, color:"#0F172A" }}>{img.description || "—"}</div>
                      {img.findings && <div style={{ fontSize:11, color:"#64748B", marginTop:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{img.findings.substring(0,80)}{img.findings.length>80?"…":""}</div>}
                    </div>
                    <div style={{ fontSize:12, color:"#64748B" }}>{img.radiologist || "—"}</div>
                    <div>
                      <button className="btn btn-secondary btn-sm" style={{ fontSize:11 }}
                        onClick={e => { e.stopPropagation(); openStudy(img); }}>{t.viewAction}</button>
                    </div>
                  </div>
                );
              })}
              <div style={{ padding:"10px 20px", borderTop:"1px solid #F1F5F9", fontSize:12, color:"#94A3B8" }}>
                {language === "bn" ? toBengaliNum(patientStudies.length, language) + " স্টাডি" : patientStudies.length + (patientStudies.length !== 1 ? " studies" : " study")}
              </div>
            </div>
          )}
        </div>

        {showUpload && (
          <UploadImagingModal
            patients={patients}
            addPatient={addPatient}
            preselectedPatient={selectedPatient}
            onClose={() => setShowUpload(false)}
            onDone={(newImg) => {
              const enriched = { ...newImg, patientName: selectedPatient.name, patientId: selectedPatient.id };
              setPatientStudies(prev => [enriched, ...prev]);
              setAllImaging(prev => [enriched, ...prev]);
              setShowUpload(false);
            }}
          />
        )}
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // LEVEL 1: Patient list
  // ════════════════════════════════════════════════════════════════════════════
  if (loading && level !== "patients") return null;
  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
        <div>
          <h1 style={{ fontSize:20, fontWeight:700, marginBottom:2 }}>{t.imagingRegister}</h1>
          <p style={{ color:"#64748B", fontSize:13 }}>{t.imagingSubtitle}</p>
        </div>
        {canUpload && (
          <button className="btn btn-primary" onClick={() => setShowUpload(true)}>{t.uploadImaging}</button>
        )}
      </div>

      {/* Stats */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:16 }}>
        {[
          { label:t.patientsWithImaging, value:stats.patients, color:"#4F46E5" },
          { label:t.totalStudies,         value:stats.studies,  color:"#0D9488" },
          { label:t.today,                value:stats.today,    color:"#059669" },
          { label:t.modalities,           value:stats.modalities,color:"#7C3AED" },
        ].map(s => (
          <div key={s.label} className="card" style={{ padding:"14px 18px" }}>
            <div style={{ fontSize:11, fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:6 }}>{s.label}</div>
            <div style={{ fontSize:28, fontWeight:800, color:s.color }}>{toBengaliNum(s.value, language)}</div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="card" style={{ padding:"14px 20px", marginBottom:12 }}>
        <div className="search-wrap">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input className="input" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder={language === "bn" ? "রোগীর নাম, EMR ID বা মোডালিটি দিয়ে খুঁজুন..." : "Search by patient name, EMR ID, or modality..."} />
        </div>
      </div>

      {/* Patient list */}
      <div className="card">
        {loading ? (
          <div className="empty-state"><p>Loading imaging records...</p></div>
        ) : patientGroups.length === 0 ? (
          <div className="empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="48" height="48" style={{ margin:"0 auto 12px", display:"block", color:"#CBD5E1" }}>
              <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
            </svg>
            <h3>{search ? (language === "bn" ? "কোনো রোগী পাওয়া যায়নি" : "No patients found") : (language === "bn" ? "কোনো ইমেজিং রেকর্ড নেই" : "No imaging records")}</h3>
            <p>{canUpload ? (language === "bn" ? "শুরু করতে একটি ইমেজিং স্টাডি আপলোড করুন।" : "Upload an imaging study to get started.") : (language === "bn" ? "কোনো ইমেজিং উপলব্ধ নেই।" : "No imaging available.")}</p>
          </div>
        ) : (
          <div>
            {/* Header */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 200px 120px 80px", padding:"10px 20px", background:"#F8F9FC", borderBottom:"2px solid #E4E8F0" }}>
              {(language === "bn" ? [t.patientCol2, t.modalitiesCol, t.lastStudy, t.studiesCol] : ["Patient","Modalities","Last Study","Studies"]).map(h => (
                <div key={h} style={{ fontSize:11, fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.5px" }}>{h}</div>
              ))}
            </div>

            {pagedGroups.map((group, idx) => (
              <div key={group.patientId} style={{
                display:"grid", gridTemplateColumns:"1fr 200px 120px 80px",
                padding:"14px 20px", borderBottom:"1px solid #F1F5F9",
                background: idx%2===0 ? "#fff" : "#FAFBFC",
                alignItems:"center", cursor:"pointer", transition:"background 0.12s",
              }}
                onMouseEnter={e => e.currentTarget.style.background="#EFF6FF"}
                onMouseLeave={e => e.currentTarget.style.background=idx%2===0?"#fff":"#FAFBFC"}
                onClick={() => openPatient(group)}
              >
                {/* Patient info */}
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <div style={{ width:36, height:36, borderRadius:"50%", background:getAvatarColor(group.patientName).bg, color:getAvatarColor(group.patientName).color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:700, flexShrink:0 }}>
                    {getInitials(group.patientName)}
                  </div>
                  <div>
                    <div style={{ fontWeight:700, fontSize:14 }}>{group.patientName}</div>
                    <div style={{ fontSize:11, color:"#94A3B8" }}>{group.patientId}</div>
                  </div>
                </div>

                {/* Modality badges */}
                <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
                  {[...group.modalities].map(m => <ModalityBadge key={m} modality={m} />)}
                </div>

                {/* Last study date */}
                <div style={{ fontSize:12, color:"#475569", fontWeight:600 }}>{group.lastDate}</div>

                {/* Study count */}
                <div style={{ fontSize:14, fontWeight:700, color:"#0F172A" }}>{group.studies.length}</div>
              </div>
            ))}

            {/* Footer + Pagination */}
            <div style={{ padding:"12px 20px", borderTop:"1px solid #F1F5F9", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div style={{ fontSize:12, color:"#94A3B8" }}>
                {language === "bn" ? `${toBengaliNum((page-1)*PAGE_SIZE+1, language)}–${toBengaliNum(Math.min(page*PAGE_SIZE, patientGroups.length), language)} এর মধ্যে ${toBengaliNum(patientGroups.length, language)} রোগী দেখানো হচ্ছে` : `Showing ${(page-1)*PAGE_SIZE+1}–${Math.min(page*PAGE_SIZE, patientGroups.length)} of ${patientGroups.length} patient${patientGroups.length!==1?"s":""}`}
              </div>
              {totalPages > 1 && (
                <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                  <button className="btn btn-ghost btn-sm" disabled={page===1} onClick={() => setPage(p=>p-1)}>← Prev</button>
                  {Array.from({ length: totalPages }, (_, i) => i+1).filter(p => Math.abs(p-page)<=2 || p===1 || p===totalPages).map((p, i, arr) => (
                    <span key={p}>
                      {i>0 && arr[i-1]!==p-1 && <span style={{ color:"#94A3B8", padding:"0 4px" }}>…</span>}
                      <button style={{ ...filterBtn(p===page), minWidth:32 }} onClick={() => setPage(p)}>{p}</button>
                    </span>
                  ))}
                  <button className="btn btn-ghost btn-sm" disabled={page===totalPages} onClick={() => setPage(p=>p+1)}>Next →</button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {showUpload && (
        <UploadImagingModal
          patients={patients}
          addPatient={addPatient}
          onClose={() => setShowUpload(false)}
          onDone={(newImg) => {
            const enriched = {
              ...newImg,
              patientName: newImg.patient_name || "",
              patientId:   newImg.patient_id   || "",
            };
            setAllImaging(prev => [enriched, ...prev]);
            setShowUpload(false);
          }}
        />
      )}
    </div>
  );
}

// ── Upload Modal (unchanged) ──────────────────────────────────────────────────
function UploadImagingModal({ patients, addPatient, preselectedPatient = null, onClose, onDone }) {
  const { addImaging } = useApp();
  const [mode, setMode]             = useState(preselectedPatient ? "search" : "search");
  const [patientSearch, setPSearch] = useState(preselectedPatient ? preselectedPatient.name : "");
  const [selectedPatient, setSelPt] = useState(preselectedPatient || null);
  const [file, setFile]             = useState(null);
  const [dragging, setDrag]         = useState(false);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState("");
  const [customModalities, setCustomModalities] = useState([]);
  const [predefinedModalities, setPredefinedModalities] = useState([]);

  useEffect(() => {
    imagingModalityAPI.list()
      .then(d => {
        setPredefinedModalities(d.predefined || []);
        setCustomModalities(d.custom || []);
      })
      .catch(() => {
        // fallback to hardcoded if API fails
        setPredefinedModalities(MODALITIES.slice(0, -1));
      });
  }, []);

  const allModalities = [...predefinedModalities, ...customModalities, "Custom..."];

  const saveCustomModality = (name) => {
    const trimmed = name.trim();
    if (!trimmed || predefinedModalities.includes(trimmed) || customModalities.includes(trimmed)) return;
    imagingModalityAPI.save(trimmed)
      .then(() => imagingModalityAPI.list())
      .then(d => {
        setPredefinedModalities(d.predefined || []);
        setCustomModalities(d.custom || []);
      })
      .catch(() => {});
  };

  const [newPt, setNewPt] = useState({ name:"", age:"", sex:"Female", blood:"B+", phoneDigits:"", doctor:"", address:"" });
  const setNP = (k,v) => setNewPt(f => ({...f,[k]:v}));

  const [form, setForm] = useState({
    modality:"X-Ray",
    date: new Date().toISOString().split("T")[0],
    time:"", description:"", findings:"", radiologist:""
  });
  const set = (k,v) => setForm(f => ({...f,[k]:v}));

  const searchedPatients = patients.filter(p => {
    const q = patientSearch.toLowerCase();
    return q.length > 0 && !preselectedPatient && (
      p.name?.toLowerCase().includes(q) ||
      p.id?.toLowerCase().includes(q) ||
      p.phone?.includes(patientSearch)
    );
  }).slice(0,8);

  const phoneResult = validatePhoneDigits(newPt.phoneDigits);

  const submit = async () => {
    setError(""); setLoading(true);
    // Save custom modality to DB so it persists across all devices
    if (form.modality && !MODALITIES.includes(form.modality)) saveCustomModality(form.modality);
    try {
      let patientId = selectedPatient?.id;
      let patientName = selectedPatient?.name;

      if (mode === "new") {
        if (!newPt.name.trim()) { setError("Patient name is required."); setLoading(false); return; }
        if (!newPt.age) { setError("Age is required."); setLoading(false); return; }
        if (!phoneResult.valid) { setError(phoneResult.message); setLoading(false); return; }
        const created = await addPatient({ ...newPt, phone: phoneResult.normalised, age: parseInt(newPt.age) });
        patientId = created;
        patientName = newPt.name;
      }

      if (!patientId) { setError("Please select or register a patient."); setLoading(false); return; }
      if (!file) { setError("Please select an image file."); setLoading(false); return; }

      const result = await addImaging(patientId, form, file);
      onDone({ ...result, patientName, patientId });
    } catch (e) {
      setError(e.message || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <div style={{ fontWeight:700, fontSize:16 }}>Upload Imaging Study</div>
          <button className="btn btn-icon btn-ghost" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-red">{error}</div>}

          {!preselectedPatient && (
            <div style={{ display:"flex", gap:0, marginBottom:16, border:"1px solid #E4E8F0", borderRadius:8, overflow:"hidden" }}>
              <button onClick={() => { setMode("search"); setSelPt(null); }} style={{ flex:1, padding:"10px", fontSize:13, fontWeight:600, cursor:"pointer", border:"none", background: mode==="search" ? "#0F172A" : "#F8F9FC", color: mode==="search" ? "#fff" : "#475569" }}>🔍 Search Existing Patient</button>
              <button onClick={() => { setMode("new"); setSelPt(null); }} style={{ flex:1, padding:"10px", fontSize:13, fontWeight:600, cursor:"pointer", border:"none", background: mode==="new" ? "#0F172A" : "#F8F9FC", color: mode==="new" ? "#fff" : "#475569", borderLeft:"1px solid #E4E8F0" }}>+ Quick Register New Patient</button>
            </div>
          )}

          {preselectedPatient ? (
            <div style={{ display:"flex", alignItems:"center", gap:10, background:"#EFF6FF", border:"1px solid #BFDBFE", borderRadius:8, padding:"10px 14px", marginBottom:16 }}>
              <div style={{ width:32, height:32, borderRadius:"50%", background:"#2563EB", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700 }}>
                {preselectedPatient.name?.charAt(0)}
              </div>
              <div style={{ fontWeight:700 }}>{preselectedPatient.name}</div>
              <div style={{ fontSize:12, color:"#64748B", marginLeft:4 }}>{preselectedPatient.id}</div>
            </div>
          ) : mode === "search" ? (
            <div className="form-group">
              <label className="form-label">Search Patient *</label>
              <div className="search-wrap" style={{ marginBottom:8 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input className="input" value={patientSearch} onChange={e => { setPSearch(e.target.value); setSelPt(null); }} placeholder="Type patient name, EMR ID or phone..." autoFocus />
              </div>
              {selectedPatient && (
                <div style={{ display:"flex", alignItems:"center", gap:10, background:"#EFF6FF", border:"1px solid #BFDBFE", borderRadius:8, padding:"10px 14px", marginBottom:8 }}>
                  <div style={{ width:32, height:32, borderRadius:"50%", background:"#2563EB", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700 }}>{selectedPatient.name?.charAt(0)}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700 }}>{selectedPatient.name}</div>
                    <div style={{ fontSize:12, color:"#64748B" }}>{selectedPatient.id} · {selectedPatient.age}y · {selectedPatient.sex}</div>
                  </div>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setSelPt(null); setPSearch(""); }}>✕</button>
                </div>
              )}
              {!selectedPatient && searchedPatients.length > 0 && (
                <div style={{ border:"1px solid #E4E8F0", borderRadius:8, overflow:"hidden", maxHeight:200, overflowY:"auto" }}>
                  {searchedPatients.map(p => (
                    <div key={p.id} style={{ padding:"10px 14px", cursor:"pointer", borderBottom:"1px solid #F1F5F9", display:"flex", alignItems:"center", gap:10 }}
                      onMouseEnter={e => e.currentTarget.style.background="#F8F9FC"}
                      onMouseLeave={e => e.currentTarget.style.background="#fff"}
                      onClick={() => { setSelPt(p); setPSearch(p.name); }}
                    >
                      <div style={{ width:28, height:28, borderRadius:"50%", background:"#EFF6FF", color:"#2563EB", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700 }}>{p.name?.charAt(0)}</div>
                      <div>
                        <div style={{ fontWeight:600, fontSize:13 }}>{p.name}</div>
                        <div style={{ fontSize:11, color:"#94A3B8" }}>{p.id} · {p.age}y · {p.sex} · {p.phone||"—"}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {!selectedPatient && patientSearch.length > 0 && searchedPatients.length === 0 && (
                <div style={{ padding:"12px 14px", color:"#94A3B8", fontSize:13, border:"1px solid #E4E8F0", borderRadius:8, textAlign:"center" }}>
                  No patient found. <button className="btn btn-ghost btn-sm" onClick={() => setMode("new")} style={{ color:"#2563EB" }}>Register as new patient →</button>
                </div>
              )}
            </div>
          ) : (
            <div>
              <div style={{ fontSize:12, color:"#64748B", marginBottom:12, padding:"8px 12px", background:"#F8F9FC", borderRadius:6 }}>Patient will be registered and imaging uploaded in one step.</div>
              <div className="form-grid-2">
                <div className="form-group"><label className="form-label">Full Name *</label><input className="input" value={newPt.name} onChange={e => setNP("name",e.target.value)} placeholder="Patient name" autoFocus /></div>
                <div className="form-group"><label className="form-label">Age *</label><input className="input" type="number" value={newPt.age} onChange={e => setNP("age",e.target.value)} placeholder="Years" min="0" max="150" /></div>
                <div className="form-group"><label className="form-label">Sex</label><select className="select" value={newPt.sex} onChange={e => setNP("sex",e.target.value)}><option>Female</option><option>Male</option><option>Other</option></select></div>
                <div className="form-group"><label className="form-label">Blood Group</label><select className="select" value={newPt.blood} onChange={e => setNP("blood",e.target.value)}>{BLOOD_GROUPS.map(b => <option key={b}>{b}</option>)}</select></div>
                <div className="form-group">
                  <label className="form-label">Phone *</label>
                  <div style={{ display:"flex" }}>
                    <span style={{ padding:"0 10px", display:"flex", alignItems:"center", background:"#F1F5F9", border:"1px solid #E4E8F0", borderRight:"none", borderRadius:"6px 0 0 6px", fontSize:13, fontWeight:700, color:"#475569" }}>+880</span>
                    <input className="input" style={{ borderRadius:"0 6px 6px 0", borderLeft:"none" }} value={newPt.phoneDigits} onChange={e => setNP("phoneDigits", e.target.value.replace(/\D/g,"").slice(0,10))} placeholder="1712345678" maxLength={10} inputMode="numeric" />
                  </div>
                  {newPt.phoneDigits && <div style={{ fontSize:11, marginTop:3, color: phoneResult.valid?"#16A34A":"#DC2626" }}>{phoneResult.valid ? `✓ ${phoneResult.normalised}` : phoneResult.message}</div>}
                </div>
                <div className="form-group"><label className="form-label">Referred Doctor</label><input className="input" value={newPt.doctor} onChange={e => setNP("doctor",e.target.value)} placeholder="Dr. Name" /></div>
              </div>
            </div>
          )}

          <div style={{ borderTop:"1px solid #E4E8F0", marginTop:16, paddingTop:16 }}>
            <div style={{ fontSize:12, fontWeight:700, color:"#475569", textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:12 }}>Imaging Details</div>
            <div className="form-grid-2">
              <div className="form-group">
                <label className="form-label">Modality *</label>
                <select className="select" value={predefinedModalities.includes(form.modality) || customModalities.includes(form.modality) ? form.modality : "Custom..."} onChange={e => {
                  if (e.target.value === "Custom...") set("modality", "");
                  else set("modality", e.target.value);
                }}>
                  {allModalities.map(m => <option key={m}>{m}</option>)}
                </select>
                {(!allModalities.includes(form.modality) || form.modality === "") && (
                  <input
                    className="input"
                    style={{ marginTop: 6 }}
                    placeholder="Enter modality name (e.g. Fluoroscopy)"
                    value={form.modality}
                    onChange={e => set("modality", e.target.value)}
                    autoFocus
                  />
                )}
              </div>
              <div className="form-group"><label className="form-label">Study Date</label><input className="input" type="date" value={form.date} onChange={e => set("date",e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Description</label><input className="input" value={form.description} onChange={e => set("description",e.target.value)} placeholder="e.g. Chest PA view" /></div>
              <div className="form-group"><label className="form-label">Radiologist</label><input className="input" value={form.radiologist} onChange={e => set("radiologist",e.target.value)} placeholder="Radiologist name" /></div>
            </div>
            <div className="form-group"><label className="form-label">Preliminary Findings</label><textarea className="input" value={form.findings} onChange={e => set("findings",e.target.value)} rows={2} placeholder="Optional..." style={{ resize:"vertical" }} /></div>
            <div className={`upload-area ${dragging?"dragging":""}`}
              onDragOver={e => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={e => { e.preventDefault(); setDrag(false); setFile(e.dataTransfer.files[0]); }}
              onClick={() => document.getElementById("img-input").click()}
            >
              <input id="img-input" type="file" accept="image/*,.pdf,.dcm" style={{ display:"none" }} onChange={e => setFile(e.target.files[0])} />
              {file ? (
                <div>
                  <div style={{ fontWeight:700, fontSize:14, color:"#4F46E5" }}>{file.name}</div>
                  <div style={{ fontSize:12, color:"#64748B", marginTop:4 }}>{(file.size/1024).toFixed(1)} KB · <strong style={{ color:"#4F46E5" }}>Click to change</strong></div>
                </div>
              ) : (
                <div>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="36" height="36" style={{ margin:"0 auto 10px", display:"block", color:"#CBD5E1" }}><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                  <div style={{ fontWeight:700, fontSize:14, color:"#0F172A", marginBottom:4 }}>Drop image here or click to browse</div>
                  <div style={{ fontSize:12, color:"#64748B" }}>Supports JPG, PNG, DICOM, PDF</div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={loading}>
            {loading ? "Uploading..." : mode==="new" ? "Register & Upload" : "Upload Imaging"}
          </button>
        </div>
      </div>
    </div>
  );
}